import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ErrorEvent, LatLng, LatLngBounds, latLngBounds, LocationEvent} from 'leaflet';
import {ErrorBoundary} from 'react-error-boundary';

import {LoadIndicator} from '../layout/loader/LoadIndicator';
import {MapModal} from '../layout/map-modal/MapModal';

import {Button} from '../form/button/Button';
import {Search} from '../form/search/Search';

import {FallbackError} from '../errors/FallbackError';

import {LeafletMapContainer} from '../map/map-container/LeaflefMapContainer';
import {PositionMarker} from '../map/marker/PositionMarker';
import {VisibleClinicsLayer} from '../map/map-container/VisibleClinicsLayer';
import {LocationErrorCodes, MapControls} from '../map/map-controls/MapControls';

import {
    commonElem,
    isLocationStopWithinBoundingBox,
    isLocationWithinBoundingbox
} from '../../services/filtering/filtering';
import {fetchPlace} from '../../services/api/api';
import {PlaceFilterMap, SearchFilterMap, useSearchFilters} from '../../hooks/useSearchFilters';

import './style.scss';
import {MoreFilters} from './more-filters/MoreFilters';
import {CountyFilters} from './county-filters/CountyFilters';
import {ClinicObjectSlim, Location, ReasonFilterObject, SearchResult} from '../../services/api/types/types';
import {FilterSkeleton} from '../layout/skeleton/filter-skeleton/FilterSkeleton';
import {SWE_BOUNDING_BOX} from '../../services/geo/constants';
import {ErrorDialog, UnionErrorType} from './error-dialog/ErrorDialog';
import {ListStatus, UiActions, UiLoadStatusActions, UiStringActions, useUiContext} from '../../context/UiContext';
import {useMapDataContext, MapLocationActions, MapBooleanActions} from '../../context/MapDataContext';
import {ListModal} from './list-modal/ListModal';
import {CSSTransition} from 'react-transition-group';
import {MapPanListener} from '../map/map-pan-listener/MapPanListener';
import {MapPolicyMenu} from '../map/map-policy-menu/MapPolicyMenu';
import {PickVisitCategory, VisitCategory} from './components/PickVisitCategory';
import {MobileSearchFooter} from './components/MobileSearchFooter';
import {SearchFooter} from './components/SearchFooter';
import {PickVisitReasons} from './reason-filters/PickVisitReasons';
import {InfoTextsDialog} from '../info-texts/info-texts-dialog/InfoTextsDialog';
import {InfoTextsControl} from '../info-texts/InfoTextsControl';
import {getTextFromLocale} from 'src/services/i18n/locale';
import {VisibleLocationsLayer} from '../map/map-container/VisibleLocationsLayer';

export type LoadStatus = 'loading' | 'done' | 'error' | 'hold';
type SearchMode = 'auto' | 'manual';

export function Main() {
    const {
        searchParams,
        placeFilters,
        setPlaceFilters,
        addFilter,
        removeFilter,
        setFilter,
        addPlaceFilter,
        removePlaceFilter,
        setOthers,
        searchParamsCount,
        clearParams,
        clearParamsByType
    } = useSearchFilters();

    const {state, dispatch: uiDispatch} = useUiContext();
    const {listStatus, popupOpenId, smallDevice, searchExtended, clinicLoadStatus, filtersLoadStatus, visibleClinics} =
        state;
    const {state: mapDataState, dispatch: mapDataDispatch} = useMapDataContext();
    const {careGivers, visitReasons, counties, clinics, locations} = mapDataState;

    const {t} = useTranslation();

    const [selectedCategory, setSelectedCategory] = useState<VisitCategory>('vaccination');
    const [filteredClinics, setFilteredClinics] = useState<Array<ClinicObjectSlim>>([]);
    const [filteredIds, setFilteredIds] = useState<Array<SearchResult>>([]);
    const [isSearchFilterActive, setIsSearchFilterActive] = useState(false);
    const [searchFilterTypes, setSearchFilterTypes] = useState<Array<SearchResult['type']>>(['CLINIC', 'LOCATION']);
    const [filteredLocations, setFilteredLocations] = useState<Array<Location>>([]);
    const [mapCenterPoint, setMapCenterPoint] = useState<LatLng | null>(null);
    const [searchIsFocused, setSearchIsFocused] = useState(false);
    const [searchResultsIsExpanded, setSearchResultsIsExpanded] = useState(false);

    const [initialFilterDone, setInitialFilterDone] = useState(false);
    const [mapBounds, setMapBounds] = useState<LatLngBounds | null>(() => {
        if (searchParams.place.length === 0 && searchParams.caregiver.length === 0) {
            // Default Sweden boundingbox
            return SWE_BOUNDING_BOX;
        }

        return null;
    });

    const [error, setError] = useState<UnionErrorType>(null);

    const mapRef = useRef<L.Map>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const previousPlaceSearchRef = useRef(searchParams.place.join(','));

    const firstLoad = useRef(true);
    const placeFilterChanged = useRef(false);

    const searchMode: SearchMode = smallDevice ? 'manual' : 'auto';
    const reasonQuery = searchParams.reason.length > 0 ? `?reason=${searchParams.reason.join('&reason=')}` : '';
    const showList = listStatus === ListStatus.VISIBLE || listStatus === ListStatus.FORCED_VISIBLE;

    // prevent loop when there is no matches in search filter
    const onSearchIdsChange = (
        ids: Array<SearchResult>,
        isActive: boolean,
        appliedTypes: Array<SearchResult['type']> = ['CLINIC', 'LOCATION']
    ) => {
        setFilteredIds(ids);
        setIsSearchFilterActive(isActive);
        setSearchFilterTypes((currentSearchFilterTypes) => {
            const sameLength = currentSearchFilterTypes.length === appliedTypes.length;
            const sameTypes = currentSearchFilterTypes.every((type) => appliedTypes.includes(type));

            if (sameLength && sameTypes) {
                return currentSearchFilterTypes;
            }

            return appliedTypes;
        });
    };

    const onLocationError = (e: ErrorEvent, retryFunction?: () => void) => {
        uiDispatch({type: UiLoadStatusActions.SET_CLINIC_LOAD_STATUS, value: 'done'});

        switch (e.code) {
            case LocationErrorCodes.TIMEOUT:
                setError(['location_timeout', retryFunction !== undefined ? retryFunction : () => {}]);
                break;
            case LocationErrorCodes.PERMISSION_DENIED:
            default:
                setError('location_permission');
        }
    };

    const onLocationFound = (e: LocationEvent) => {
        mapDataDispatch({type: MapLocationActions.SET_USER_LOCATION, value: e.latlng});
        mapDataDispatch({type: MapBooleanActions.SET_HAS_GEO_PERMISSION, value: true});
        uiDispatch({type: UiLoadStatusActions.SET_CLINIC_LOAD_STATUS, value: 'done'});
    };

    const onLocationSearch = () => {
        mapDataDispatch({type: MapLocationActions.SET_USER_LOCATION, value: null});
        uiDispatch({type: UiLoadStatusActions.SET_CLINIC_LOAD_STATUS, value: 'loading'});
    };

    // Apply loading state to initiate filter and clinics loading
    const onMapReady = () => {
        uiDispatch({type: UiLoadStatusActions.SET_CLINIC_LOAD_STATUS, value: 'loading'});
        uiDispatch({type: UiLoadStatusActions.SET_FILTERS_LOAD_STATUS, value: 'loading'});
    };

    const filterClinicsAndLocations = useCallback(
        (
            searchParams: SearchFilterMap,
            filteredIds: SearchResult[],
            isSearchFilterActive: Boolean,
            searchFilterTypes: Array<SearchResult['type']>
        ) => {
            setError(null);

            // TODO: Put this in a better place, also probably optimize somehow
            const filtered = clinics.filter((clinic) => {
                const shouldInclude = [];
                if (searchParams.place.length > 0) {
                    shouldInclude.push(
                        searchParams.place.some((placeId) => {
                            if (!placeFilters[placeId]) {
                                return true;
                            }
                            return isLocationWithinBoundingbox(placeFilters[placeId].boundingBox, clinic);
                        })
                    );
                }
                if (searchParams.other.includes('hasDropin')) {
                    shouldInclude.push(clinic.hasDropin);
                }
                if (searchParams.clinic.length) {
                    shouldInclude.push(searchParams.clinic.includes(clinic.id));
                } else if (searchParams.location.length) {
                    shouldInclude.push(false);
                }
                if (searchParams.caregiver.length) {
                    const foundSelf = searchParams.caregiver.includes(clinic.careGiverId?.toString());
                    const foundUG = searchParams.caregiver.includes(clinic.principalCareGiverId?.toString());
                    shouldInclude.push(foundSelf || foundUG);
                }
                if (searchParams.reason.length) {
                    shouldInclude.push(
                        commonElem(searchParams.reason, clinic.availableVisitReasonIds).length ===
                            searchParams.reason.length
                    );
                }
                if (isSearchFilterActive && searchFilterTypes.includes('CLINIC')) {
                    const filteredClinicIds = filteredIds
                        .filter((f) => f.type === 'CLINIC')
                        .map((f) => f.id.toString());
                    shouldInclude.push(filteredClinicIds.includes(clinic.id));
                }

                return !shouldInclude.includes(false);
            });

            setFilteredClinics(filtered);

            const filteredLocs = locations.filter((location) => {
                if (searchParams.place.length > 0) {
                    if (
                        !searchParams.place.some((placeId) => {
                            if (!placeFilters[placeId]) {
                                return true;
                            }
                            return isLocationStopWithinBoundingBox(placeFilters[placeId].boundingBox, location);
                        })
                    )
                        return false;
                }
                if (searchParams.location.length > 0) {
                    if (!searchParams.location.includes(location.id.toString())) return false;
                } else if (searchParams.clinic.length > 0) {
                    return false;
                }
                if (searchParams.caregiver.length) {
                    if (!searchParams.caregiver.includes(location.careGiver?.toString())) return false;
                }
                if (isSearchFilterActive && searchFilterTypes.includes('LOCATION')) {
                    const filteredLocationIds = filteredIds.filter((f) => f.type === 'LOCATION').map((f) => f.id);
                    if (!filteredLocationIds.includes(location.id)) return false;
                }
                return true;
            });

            setFilteredLocations(filteredLocs);

            const boundsPoints: Array<[number, number]> = [
                ...filtered.map((clinic) => [clinic.location.lat, clinic.location.lng] as [number, number]),
                ...filteredLocs.map((location) => [location.position.lat, location.position.lon] as [number, number])
            ];

            // Initial setting of viewport when searchparams are present
            const hasSearchParams =
                searchParams.reason.length +
                    searchParams.other.length +
                    searchParams.place.length +
                    searchParams.caregiver.length >
                0;
            if (firstLoad.current === true && hasSearchParams && boundsPoints.length > 0) {
                setMapBounds(latLngBounds(boundsPoints));
                if (!smallDevice) {
                    uiDispatch({type: UiStringActions.SET_LIST_STATUS, value: ListStatus.FORCED_VISIBLE});
                }
            }
            firstLoad.current = false;

            // Place filter was added since previous filter, set viewport
            if (placeFilterChanged.current === true) {
                placeFilterChanged.current = false;

                if (mapRef.current) {
                    mapRef.current.closePopup();
                }
                if (boundsPoints.length > 0) {
                    setMapBounds(latLngBounds(boundsPoints));
                }
            }
            if (!hasSearchParams) {
                setMapBounds(SWE_BOUNDING_BOX);
            }

            if (filtered.length === 0 && filteredLocs.length === 0) {
                setError('empty_result');
            }
        },
        [clinics, placeFilters, locations]
    );

    useEffect(() => {
        const descriptionElement = document.querySelector('meta[name=description]');

        const findVisitReasonsByCategory = (visitReasons: ReasonFilterObject[], category: string) => {
            const visitReasonsToAdd = visitReasons.filter(
                (visitReason: ReasonFilterObject) => visitReason?.category === category
            );

            return visitReasonsToAdd.map((visitReasonToAdd) => getTextFromLocale(visitReasonToAdd?.name));
        };

        const findVisitReasons = (
            visitReasonsOnClinic: ReasonFilterObject[],
            visitReasonCategory: string,
            serviceText: string,
            servicesText: string[],
            itemLimit = 50
        ) => {
            let categoryVisitReasons = findVisitReasonsByCategory(visitReasonsOnClinic, visitReasonCategory);

            if (categoryVisitReasons.length > 0) {
                if (categoryVisitReasons.length > itemLimit) {
                    categoryVisitReasons.length = itemLimit; // Limit number of items
                }

                const categoryVisitReasonsText = categoryVisitReasons
                    .join(', ')
                    .replace(/,(?!.*,)/gim, ' ' + t('meta.and'));
                const text = serviceText + ' (' + categoryVisitReasonsText + ')';
                servicesText.push(text);
            }
            return servicesText;
        };

        if (descriptionElement) {
            let servicesText: string[] = [];

            let descriptionText: string = t('meta.description.main_map') ?? '';

            servicesText = findVisitReasons(visitReasons, 'vaccination', t('meta.vaccination'), servicesText);
            servicesText = findVisitReasons(visitReasons, 'travel', t('meta.travel'), servicesText);
            servicesText = findVisitReasons(visitReasons, 'other', t('meta.other'), servicesText);

            if (servicesText.length > 0) {
                const servicesTextCombined = servicesText.join(', ').replace(/\),(?!.*\),)/gim, ') ' + t('meta.and'));
                descriptionText += ' ' + t('meta.description.main_map_offer') + servicesTextCombined;
            }

            descriptionElement?.setAttribute('content', descriptionText);
        }
    }, [visitReasons]);

    useEffect(() => {
        if (smallDevice || firstLoad.current === true) {
            return;
        }

        if (listStatus === ListStatus.HIDDEN && visibleClinics.length <= 50) {
            uiDispatch({type: UiStringActions.SET_LIST_STATUS, value: ListStatus.FORCED_VISIBLE});
        }
    }, [visibleClinics.length, listStatus, uiDispatch, smallDevice]);

    useEffect(() => {
        if (filtersLoadStatus === 'hold' || filtersLoadStatus === 'loading') {
            return;
        }

        const firstSelectedReason = visitReasons.find(({id}) => {
            return searchParams.reason.includes(id);
        });
        if (firstSelectedReason) {
            setSelectedCategory(firstSelectedReason.category);
        }
    }, [filtersLoadStatus, searchParams.reason, visitReasons]);

    const toggleSearchView = useCallback(() => {
        if (smallDevice) {
            scrollRef.current?.scrollTo({top: 0});
            uiDispatch({type: UiActions.SET_EXTEND_SEARCH, value: !searchExtended});
        } else {
            uiDispatch({type: UiActions.SET_EXTEND_SEARCH, value: true});
        }
    }, [searchExtended, smallDevice, uiDispatch]);

    useEffect(() => {
        if (smallDevice) {
            uiDispatch({type: UiActions.SET_EXTEND_SEARCH, value: false});
        }
        return () => {
            uiDispatch({type: UiStringActions.SET_LIST_STATUS, value: ListStatus.HIDDEN});
        };
    }, [smallDevice, uiDispatch]);

    useEffect(() => {
        const placeSearchKey = searchParams.place.join(',');

        if (firstLoad.current) {
            previousPlaceSearchRef.current = placeSearchKey;
            return;
        }

        if (previousPlaceSearchRef.current !== placeSearchKey) {
            placeFilterChanged.current = true;
            previousPlaceSearchRef.current = placeSearchKey;
        }
    }, [searchParams.place]);

    // Load missing place filters on load and when place params change
    useEffect(() => {
        if (filtersLoadStatus === 'loading' || filtersLoadStatus === 'hold') {
            return;
        }

        const missingPlaceIds = searchParams.place.filter((placeId) => !placeFilters[placeId]);

        if (missingPlaceIds.length === 0) {
            if (filtersLoadStatus !== 'done') {
                uiDispatch({type: UiLoadStatusActions.SET_FILTERS_LOAD_STATUS, value: 'done'});
            }
            return;
        }

        const loadFilters = async () => {
            uiDispatch({type: UiLoadStatusActions.SET_FILTERS_LOAD_STATUS, value: 'loading'});

            try {
                const nextPlaceFilters: PlaceFilterMap = {...placeFilters};
                for (let i = 0; i < missingPlaceIds.length; i++) {
                    const placeId = missingPlaceIds[i];
                    nextPlaceFilters[placeId] = await fetchPlace(placeId);
                }

                setPlaceFilters(nextPlaceFilters);
                uiDispatch({type: UiLoadStatusActions.SET_FILTERS_LOAD_STATUS, value: 'done'});
            } catch (error) {
                uiDispatch({type: UiLoadStatusActions.SET_FILTERS_LOAD_STATUS, value: 'error'});
            }
        };

        loadFilters();
    }, [filtersLoadStatus, searchParams.place, placeFilters, setPlaceFilters, uiDispatch]);

    useEffect(() => {
        if ((clinics.length === 0 && locations.length === 0) || ['loading', 'hold'].includes(filtersLoadStatus)) {
            return;
        }

        const hasUnresolvedPlaceFilters = searchParams.place.some((placeId) => !placeFilters[placeId]);
        if (hasUnresolvedPlaceFilters) {
            return;
        }

        if (!initialFilterDone) {
            setInitialFilterDone(true);
        }

        if (!isSearchFilterActive) {
            return;
        }

        filterClinicsAndLocations(searchParams, filteredIds, isSearchFilterActive, searchFilterTypes);
    }, [
        clinics.length,
        filterClinicsAndLocations,
        filtersLoadStatus,
        initialFilterDone,
        placeFilters,
        searchMode,
        searchParams,
        filteredIds,
        isSearchFilterActive,
        searchFilterTypes
    ]);

    return (
        <>
            <main id='main-map-container'>
                <h1 id='main-title'>MittVaccin.se - Sveriges största oberoende vaccinationstjänst</h1>
                <ErrorBoundary
                    FallbackComponent={FallbackError}
                    onReset={() => {
                        /** reset the state of your app so the error doesn't happen again */
                    }}
                >
                    <InfoTextsDialog />

                    <div className={`info_texts_control ${showList ? 'info_texts_control--shifted' : ''}`}>
                        <InfoTextsControl />
                    </div>

                    <div className={`${showList ? 'main--list-modal-visible' : ''}`}>
                        <LoadIndicator show={clinicLoadStatus === 'loading' || filtersLoadStatus === 'loading'} />

                        {(clinicLoadStatus === 'error' || error !== null) && (
                            <ErrorDialog error={error} clear={() => setError(null)} />
                        )}

                        <MapModal
                            id='search-modal'
                            className={`search_modal${searchExtended ? ' search_modal--up' : ''} ${
                                popupOpenId ? 'search_modal--hide' : ''
                            }`}
                        >
                            {['loading', 'hold'].includes(filtersLoadStatus) && (
                                <section className='map_modal__content search_modal__skeleton'>
                                    <FilterSkeleton />
                                </section>
                            )}

                            {filtersLoadStatus === 'error' && (
                                <div className='map_modal__content search_modal__error'>
                                    <p>
                                        <b>{t('error_filter_loading_title.text')}</b>
                                        <br />
                                        {t('error_filter_loading_message.text')}
                                    </p>
                                    <Button
                                        tabIndex={searchExtended ? 0 : -1}
                                        onClick={() =>
                                            uiDispatch({
                                                type: UiLoadStatusActions.SET_FILTERS_LOAD_STATUS,
                                                value: 'loading'
                                            })
                                        }
                                    >
                                        {t('button_try_again.text')}
                                    </Button>
                                </div>
                            )}

                            {filtersLoadStatus === 'done' && (
                                <>
                                    <div
                                        id='search-modal-scroll'
                                        ref={scrollRef}
                                        className='map_modal__scroll'
                                        aria-hidden={searchExtended === false}
                                    >
                                        <div id='search-modal' className='map_modal__content search_modal__search'>
                                            <Search
                                                searchParams={searchParams}
                                                reasonFilters={visitReasons}
                                                placeFilters={placeFilters}
                                                clinicFilters={clinics}
                                                careGiverFilters={careGivers}
                                                locationFilters={locations}
                                                includeDropIn={searchParams.other.includes('hasDropin')}
                                                onSearchIdsChange={onSearchIdsChange}
                                                disableTabNavigation={false}
                                                onResultsExpanded={(isActive) => {
                                                    if (isActive !== searchResultsIsExpanded) {
                                                        setSearchResultsIsExpanded(isActive);
                                                    }
                                                }}
                                                onFocusUpdate={(isFocused) => {
                                                    isFocused &&
                                                        uiDispatch({
                                                            type: UiActions.SET_EXTEND_SEARCH,
                                                            value: true
                                                        });
                                                    setSearchIsFocused(isFocused);
                                                }}
                                                onClick={() => {
                                                    uiDispatch({
                                                        type: UiActions.SET_EXTEND_SEARCH,
                                                        value: !searchExtended
                                                    });
                                                }}
                                                onToggleFilter={(type, filterId, active) => {
                                                    if (type === 'reason') {
                                                        const reasonCategory = visitReasons.find(
                                                            ({id}) => id === filterId
                                                        )?.category;
                                                        // Clear all other reason filters when reason category changes
                                                        if (reasonCategory !== selectedCategory) {
                                                            setFilter('reason', [filterId]);
                                                        } else {
                                                            active
                                                                ? addFilter('reason', filterId)
                                                                : removeFilter('reason', filterId);
                                                        }
                                                    } else {
                                                        active
                                                            ? addFilter(type, filterId)
                                                            : removeFilter(type, filterId);
                                                    }
                                                }}
                                                onAddPlaceFilter={(filterId, place) => {
                                                    placeFilterChanged.current = true;
                                                    addPlaceFilter(filterId, place);
                                                }}
                                                onRemovePlaceFilter={(filterId) => {
                                                    placeFilterChanged.current = true;
                                                    removePlaceFilter(filterId);
                                                }}
                                            />
                                        </div>
                                        {!searchResultsIsExpanded && (
                                            <div>
                                                <PickVisitCategory
                                                    selectedCategory={selectedCategory}
                                                    onChange={(visitCategory) => {
                                                        if (visitCategory === selectedCategory) {
                                                            return;
                                                        }
                                                        setSelectedCategory(visitCategory);

                                                        if (searchParams.reason.length > 0) {
                                                            clearParamsByType('reason');
                                                        }
                                                    }}
                                                />

                                                {selectedCategory !== null && (
                                                    <PickVisitReasons
                                                        selectedCategory={selectedCategory}
                                                        searchParams={searchParams}
                                                        addReasonFilter={(id) => addFilter('reason', id)}
                                                        removeReasonFilter={(id) => removeFilter('reason', id)}
                                                    />
                                                )}

                                                <fieldset className='map_modal__content'>
                                                    <legend className='off-screen'>{t('county_heading.text')}</legend>
                                                    <h2>{t('county_heading.text')}</h2>
                                                    <CountyFilters
                                                        countyFilters={counties}
                                                        activeFilters={searchParams.place}
                                                        disableTabNavigation={false}
                                                        onToggleFilter={(id, place, checked) => {
                                                            placeFilterChanged.current = true;
                                                            if (checked) {
                                                                addPlaceFilter(id, place);
                                                            } else {
                                                                removePlaceFilter(id);
                                                            }
                                                        }}
                                                    />
                                                </fieldset>
                                                <fieldset className='map_modal__content'>
                                                    <legend className='off-screen'>{t('more_options.text')}</legend>
                                                    <h2>{t('more_options.text')}</h2>
                                                    <MoreFilters
                                                        activeFilters={searchParams.other}
                                                        disableTabNavigation={false}
                                                        onToggleFilter={(activeFilters) => setOthers(activeFilters)}
                                                    />
                                                </fieldset>
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        className='map_modal__content search_modal__footer'
                                        aria-hidden={searchExtended === false}
                                    >
                                        {searchMode === 'manual' && !searchIsFocused && !searchResultsIsExpanded && (
                                            <MobileSearchFooter
                                                clearParams={() => {
                                                    setSelectedCategory('vaccination');
                                                    clearParamsByType('reason');
                                                    clearParams();
                                                }}
                                                paramsCount={searchParamsCount()}
                                                toggleSearchView={toggleSearchView}
                                            />
                                        )}
                                        {searchMode === 'auto' && searchParamsCount() > 0 && (
                                            <SearchFooter
                                                clearParams={() => {
                                                    setSelectedCategory('vaccination');
                                                    clearParamsByType('reason');
                                                    clearParams();
                                                }}
                                                paramsCount={searchParamsCount()}
                                                toggleSearchView={toggleSearchView}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </MapModal>
                        <CSSTransition
                            unmountOnExit
                            timeout={{enter: 0, exit: 200}}
                            in={showList}
                            classNames={'list_modal'}
                        >
                            <ListModal mapCenterPoint={mapCenterPoint} reasonParams={searchParams.reason} />
                        </CSSTransition>
                    </div>
                    <LeafletMapContainer
                        ref={mapRef}
                        onReady={onMapReady}
                        mapBounds={mapBounds}
                        listVisible={!smallDevice && showList && searchParams.place.length > 0}
                    >
                        <MapControls
                            onLocationFound={onLocationFound}
                            onLocationSearch={onLocationSearch}
                            onLocationError={onLocationError}
                        />
                        <MapPanListener onMoveEnd={(centerPoint) => setMapCenterPoint(centerPoint)} />
                        <MapPolicyMenu />
                        <VisibleClinicsLayer reasonQuery={reasonQuery} clinics={filteredClinics} />
                        <VisibleLocationsLayer reasonQuery={reasonQuery} locations={filteredLocations} />

                        {mapDataState.userLocation && <PositionMarker position={mapDataState.userLocation} />}
                    </LeafletMapContainer>
                </ErrorBoundary>
            </main>
        </>
    );
}
